import React, { useState } from 'react';
import SharedHeaderHomeowner from './SharedHeaderHomeowner';

interface TwilioOptInPageProps {
  onNavigate: (screen: string) => void;
}

const TwilioOptInPage: React.FC<TwilioOptInPageProps> = ({ onNavigate }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    agreeToTerms: true,
    agreeToTextMessages: true
  });

  const [formErrors, setFormErrors] = useState<{
    name?: string;
    email?: string;
    phone?: string;
  }>({});
  const [showErrors, setShowErrors] = useState(false);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateName = (name: string) => {
    if (!name || name.trim().length < 2) {
      return 'Name must be at least 2 characters long';
    }
    return '';
  };

  const validateEmail = (email: string) => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validatePhone = (phone: string) => {
    if (!phone || !/^\+?\d[\d\s\-()]+$/.test(phone)) {
      return 'Please enter a valid phone number';
    }
    return '';
  };

  const canProceed = () => {
    return (
      !validateName(formData.name) &&
      !validateEmail(formData.email) &&
      !validatePhone(formData.phone) &&
      formData.agreeToTerms &&
      formData.agreeToTextMessages
    );
  };

  const handleSubmit = async () => {
    const errors = {
      name: validateName(formData.name) || undefined,
      email: validateEmail(formData.email) || undefined,
      phone: validatePhone(formData.phone) || undefined
    };

    setFormErrors(errors);
    setShowErrors(true);

    if (errors.name || errors.email || errors.phone || !formData.agreeToTerms || !formData.agreeToTextMessages) {
      return;
    }

    // Success - redirect to landing page
    onNavigate('landing');
  };

  return (
    <div className="container light-mode-override">
      <style>{`
        input[type="checkbox"]:checked {
          background-color: white !important;
          border-color: #d1d5db !important;
        }
        #q8-terms, #q8-text {
          width: 16px !important;
          height: 16px !important;
          min-width: 16px !important;
          min-height: 16px !important;
          max-width: 16px !important;
          max-height: 16px !important;
          transform: scale(0.75) !important;
          transform-origin: left center !important;
          appearance: none; -webkit-appearance: none; -moz-appearance: none;
          border: 1px solid #d1d5db; border-radius: 2px;
          background-color: white; cursor: pointer; margin: 0;
        }
        #q8-terms:checked, #q8-text:checked {
          background-color: white !important;
          border-color: #d1d5db !important;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23000000' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M6 10l3 3 6-6'/%3e%3c/svg%3e") !important;
          background-size: 100% 100% !important;
          background-position: center !important;
          background-repeat: no-repeat !important;
          box-shadow: none !important;
        }
      `}</style>
      <SharedHeaderHomeowner onNavigate={onNavigate} />

      <div className="content">
        <div style={{ maxWidth: 600, margin: '0 auto', padding: '20px' }}>
          <h2 style={{ textAlign: 'center', marginBottom: 16 }}>Get Your Roofing Estimates</h2>
          
          {/* Question 9: Contact Information with SMS Opt-In */}
          <div className="question-container" style={{ minHeight: '300px', justifyContent: 'flex-start', padding: '16px 0' }}>
            <h3 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>Contact Information</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '14px' }}>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`form-control ${formErrors.name ? 'error' : ''}`}
                  placeholder="Full Name"
                  style={{ padding: '8px 12px', fontSize: '14px', width: '100%' }}
                />
                {formErrors.name && showErrors && (
                  <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '2px' }}>
                    ⚠️ {formErrors.name}
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '14px' }}>Email Address *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`form-control ${formErrors.email ? 'error' : ''}`}
                  placeholder="your.email@example.com"
                  style={{ padding: '8px 12px', fontSize: '14px', width: '100%' }}
                />
                {formErrors.email && showErrors && (
                  <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '2px' }}>
                    ⚠️ {formErrors.email}
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '14px' }}>Phone Number *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={`form-control ${formErrors.phone ? 'error' : ''}`}
                  placeholder="(555) 555-5555"
                  style={{ padding: '8px 12px', fontSize: '14px', width: '100%' }}
                />
                {formErrors.phone && showErrors && (
                  <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '2px' }}>
                    ⚠️ {formErrors.phone}
                  </div>
                )}
              </div>
              
              {/* Terms Checkbox */}
              <div style={{ marginTop: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '13px', fontWeight: '400' }}>
                  <input
                    id="q8-terms"
                    type="checkbox"
                    checked={formData.agreeToTerms}
                    onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                    required
                    style={{ 
                      width: '12px', 
                      height: '12px', 
                      accentColor: '#2563eb',
                      cursor: 'pointer',
                      margin: '0'
                    }}
                  />
                  <span style={{ color: '#1f2937' }}>I Agree to the <a href="/terms" style={{ color: '#2563eb', textDecoration: 'underline', cursor: 'pointer' }} target="_blank" rel="noopener noreferrer">Terms & Conditions</a> *</span>
                </label>
              </div>
              
              {/* SMS Opt-In Checkbox */}
              <div style={{ marginTop: 4 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '13px', fontWeight: '400' }}>
                  <input
                    id="q8-text"
                    type="checkbox"
                    checked={formData.agreeToTextMessages}
                    onChange={(e) => handleInputChange('agreeToTextMessages', e.target.checked)}
                    style={{ 
                      width: '12px', 
                      height: '12px', 
                      accentColor: '#2563eb',
                      cursor: 'pointer',
                      margin: '0'
                    }}
                  />
                  <span style={{ color: '#1f2937' }}>I Agree to receive text messages</span>
                </label>
              </div>
              
              {/* SMS Opt-In Disclosure */}
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6, lineHeight: '1.3' }}>
                By entering your number & checking "I agree to receive text messages", you agree to receive automated texts from CoreBids, Inc. at 877-570-7485 for customer care, project updates, and support. Msg frequency varies. Msg & data rates may apply. Text STOP to cancel, HELP for help. See <a href="/terms" style={{ color: '#2563eb', textDecoration: 'underline', cursor: 'pointer' }} target="_blank" rel="noopener noreferrer">Terms & Conditions</a> and <a href="#" onClick={(e) => { e.preventDefault(); onNavigate('privacy-policy'); }} style={{ color: '#2563eb', textDecoration: 'underline', cursor: 'pointer' }} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
            <button 
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={!canProceed()}
              style={{ padding: '12px 24px' }}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwilioOptInPage;

